import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { jsPDF } from "jspdf";
import { Award, Lock, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

export default function Certification() {
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);

  useEffect(() => { api.get("/certification").then((r) => setCert(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (cert?.certified) {
      const end = Date.now() + 1200;
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0 }, colors: ["#7c3aed", "#8b5cf6", "#f59e0b"] });
        confetti({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1 }, colors: ["#7c3aed", "#8b5cf6", "#f59e0b"] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [cert]);

  const download = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const cx = W / 2;
    const violet = [124, 58, 237];
    const amber = [245, 158, 11];
    const slate = [15, 23, 42];
    const gray = [100, 116, 139];

    // background
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, W, H, "F");
    // outer border
    doc.setDrawColor(...violet);
    doc.setLineWidth(6);
    doc.rect(22, 22, W - 44, H - 44);
    // inner thin border
    doc.setDrawColor(196, 181, 253);
    doc.setLineWidth(1.2);
    doc.rect(34, 34, W - 68, H - 68);

    // brand
    doc.setTextColor(...violet);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("CAMPBELL CONNECT", cx, 96, { align: "center" });
    doc.setTextColor(...gray);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("WORKFORCE ONBOARDING PORTAL", cx, 114, { align: "center" });

    // title
    doc.setTextColor(...slate);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(38);
    doc.text("Certificate of Completion", cx, 178, { align: "center" });

    // amber divider
    doc.setDrawColor(...amber);
    doc.setLineWidth(2);
    doc.line(cx - 70, 196, cx + 70, 196);

    doc.setTextColor(...gray);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(14);
    doc.text("This certifies that", cx, 240, { align: "center" });

    // name
    doc.setTextColor(...slate);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(34);
    doc.text(cert.name || "", cx, 286, { align: "center" });

    doc.setTextColor(...gray);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(14);
    doc.text("has successfully earned the credential of", cx, 322, { align: "center" });

    // credential
    doc.setTextColor(...violet);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text(cert.credential || "", cx, 356, { align: "center" });

    // score + date row
    doc.setTextColor(51, 65, 85);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.text(`Assessment Score: ${cert.score}%          Date Issued: ${cert.date}`, cx, 400, { align: "center" });

    // footer: credential id + org
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.line(90, H - 96, W - 90, H - 96);
    doc.setTextColor(...gray);
    doc.setFontSize(11);
    doc.text(`Credential ID: ${cert.credential_id || ""}`, 96, H - 72);
    doc.text("Campbell Communications LLC", W - 96, H - 72, { align: "right" });
    doc.text("Verify authenticity with the Credential ID above.", cx, H - 50, { align: "center" });

    doc.save("campbell-connect-certificate.pdf");
  };

  if (!cert) return <div className="text-sm text-slate-400">Loading…</div>;

  if (!cert.certified) {
    return (
      <Card className="mx-auto max-w-xl text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100"><Lock className="h-6 w-6 text-slate-400" /></div>
        <h2 className="mt-4 text-lg font-bold text-slate-900">Not certified yet</h2>
        <p className="mt-2 text-sm text-slate-500">Pass the final assessment to earn your certificate.</p>
        <Button onClick={() => navigate("/assessment")} className="btn-primary-gradient mt-6 border-0 text-white">Take the assessment</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl border-4 border-violet-300 bg-white p-10 text-center shadow-lg">
        <div className="absolute inset-2 rounded-xl border border-violet-100" />
        <div className="relative">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-violet-100"><Award className="h-8 w-8 text-violet-600" /></div>
          <div className="mt-4 text-xs font-bold uppercase tracking-widest text-violet-600">Campbell Connect</div>
          <h1 className="mt-2 text-3xl font-extrabold text-slate-900">Certificate of Completion</h1>
          <p className="mt-4 text-sm text-slate-500">This certifies that</p>
          <div className="mt-1 text-2xl font-bold text-slate-900">{cert.name}</div>
          <p className="mt-3 text-sm text-slate-500">has earned the credential of</p>
          <div className="mt-1 text-lg font-bold text-violet-600">{cert.credential}</div>
          <div className="mt-4 text-sm text-slate-600">Score {cert.score}% — {cert.date}</div>
          {cert.credential_id && <div className="mt-2 text-xs font-medium text-slate-400">Credential ID: {cert.credential_id}</div>}
        </div>
      </div>
      <div className="flex justify-center gap-3">
        <Button onClick={download} data-testid="download-certificate-btn" className="btn-primary-gradient gap-2 border-0 text-white"><Download className="h-4 w-4" /> Download PDF certificate</Button>
        <Button variant="outline" onClick={() => navigate("/opportunities")} data-testid="explore-opportunities-btn">Explore opportunities</Button>
      </div>
    </div>
  );
}
